/**
 * Connector Service
 *
 * HTTP client for calling the connector proxy function.
 */

import type { AutocompleteResult, FetchResult } from '@hopdrive/sdk-configs'

// Re-export canonical types for consumers
export type { AutocompleteResult, FetchResult }

interface ProxyResponse<T> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
  }
  timing?: {
    total: number
  }
}

// =============================================================================
// Token Helper
// =============================================================================

/**
 * Get the current user's auth token.
 * Imported dynamically to avoid circular dependencies.
 */
async function getAuthToken(): Promise<string | null> {
  try {
    // Dynamic import to avoid circular dependency
    const { getNonReactiveState } = await import('@store')
    const state = getNonReactiveState()

    if (!state.user?.getToken) {
      return null
    }

    return await state.user.getToken()
  } catch (error) {
    console.error('[Connector] Failed to get auth token:', error)
    return null
  }
}

// =============================================================================
// Service Functions
// =============================================================================

/**
 * Call the connector proxy for autocomplete suggestions.
 *
 * @param triggerType - The autocomplete trigger type (e.g., 'consumer-name')
 * @param searchQuery - The user's search input
 * @returns Autocomplete results or empty array on error (silent failure)
 */
export async function autocomplete(
  triggerType: string,
  searchQuery: string
): Promise<AutocompleteResult[]> {
  try {
    const token = await getAuthToken()
    if (!token) {
      console.warn('[Connector] No auth token available for autocomplete')
      return []
    }

    const response = await fetch('/.netlify/functions/connectorProxy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'autocomplete',
        triggerType,
        searchQuery,
      }),
    })

    if (!response.ok) {
      console.error(`[Connector] Autocomplete HTTP error: ${response.status}`)
      return []
    }

    const result: ProxyResponse<AutocompleteResult[]> = await response.json()

    if (!result.success || !result.data) {
      // Silent failure - return empty results
      return []
    }

    return result.data
  } catch (error) {
    // Silent failure - log but don't throw
    console.error('[Connector] Autocomplete error:', error)
    return []
  }
}

/**
 * Call the connector proxy to fetch detailed data.
 *
 * @param triggerType - The fetch trigger type (e.g., 'contract-detail')
 * @param lookupValue - The value to look up (e.g., contract ID)
 * @returns Fetch result or null on error (silent failure)
 */
export async function fetchData(
  triggerType: string,
  lookupValue: string
): Promise<FetchResult | null> {
  try {
    const token = await getAuthToken()
    if (!token) {
      console.warn('[Connector] No auth token available for fetch')
      return null
    }

    const response = await fetch('/.netlify/functions/connectorProxy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'fetch',
        triggerType,
        lookupValue,
      }),
    })

    if (!response.ok) {
      console.error(`[Connector] Fetch HTTP error: ${response.status}`)
      return null
    }

    const result: ProxyResponse<FetchResult> = await response.json()

    if (!result.success || !result.data) {
      // Silent failure
      return null
    }

    return result.data
  } catch (error) {
    // Silent failure - log but don't throw
    console.error('[Connector] Fetch error:', error)
    return null
  }
}

// =============================================================================
// Default Export
// =============================================================================

const connectorService = {
  autocomplete,
  fetchData,
}

export default connectorService
